import React, {useState, useContext, useRef, useLayoutEffect} from 'react';

import {
  Button,
  ButtonWithPopOut,
  constants,
  useWindowSize,
} from '@newsela/angelou';
import {disableBodyScroll, enableBodyScroll} from 'body-scroll-lock';
import {css} from 'emotion';
import PropTypes from 'prop-types';
import {useSelector} from 'react-redux';

import withTrackEvent from 'static/three-oh/src/components/with/WithTrackEvent';
import {searchRoot} from 'static/three-oh/src/modules/constants/routePaths';
import {getCollections} from 'static/three-oh/src/modulesV2/selectors';
import {
  AGATHA_PAGE_SEARCH_FORM_ID,
  GLOBAL_NAV_SEARCH_FORM_ID
} from 'static/three-oh/src/routes/Search/constants';
import {ReactComponent as AssignmentsIcon} from 'staticImages/icons/globalNavIcons/assignments.svg';
import {ReactComponent as BinderIcon} from 'staticImages/icons/globalNavIcons/binder.svg';
import {ReactComponent as YourContentIcon} from 'staticImages/icons/globalNavIcons/bookmark-o.svg';
import {ReactComponent as BrowseIcon} from 'staticImages/icons/globalNavIcons/browse.svg';
import {ReactComponent as SearchIcon} from 'staticImages/icons/globalNavIcons/search.svg';

import useFocusOut from '../../../../utils/useFocusOut';
import {INACTIVE_INDEX_VALUE} from '../constants';
import {GlobalLayoutRefsContext} from '../GlobalLayoutRefsContext';
import BinderDrawer from './DrawerContents/BinderDrawer';
import BrowseDrawer from './DrawerContents/BrowseDrawer';
import YourContentDrawer from './DrawerContents/YourContentDrawer';
import GlobalNavMenuItem from './GlobalNavMenuItem';
import GlobalNavSearch from './GlobalNavSearch/GlobalNavSearch';
import {
  assignmentIconStyle,
  buttonStyles,
  globalNavStyle,
  iconStyle,
  buttonWithPopOutStyle,
  liStyle,
  topLevelLinkStyle,
  ulStyle,
  inactiveButtonStyle,
  searchFormStyle,
} from './style';

const propTypes = {
  hideGlobalNav: PropTypes.bool,
  history: PropTypes.object,
  user: PropTypes.object.isRequired,
  userProperties: PropTypes.object.isRequired,
};

const ButtonWithTrackEvent = withTrackEvent(Button);
const ButtonWithPopOutWithTrackEvent = withTrackEvent(ButtonWithPopOut);
const {
  legacy_breakpoints: {
    Breakpoints: {sm, xs},
  },
} = constants;

export const AnchorWithTrackEvent = withTrackEvent((props) => (
  <a {...props}>{props.children}</a>
));

export const GlobalNav = React.forwardRef((props, ref) => {
  GlobalNav.displayName = 'GlobalNav';

  const {hideGlobalNav, history, user, userProperties} = props;

  /**
   * Each button in the nav has an index number.
   * The onClick handler sets the openIndex to the button's index.
   * If openIndex === INACTIVE_INDEX_VALUE, all nav drawers are closed.
   */
  const [openIndex, setOpenIndex] = useState(INACTIVE_INDEX_VALUE);
  const {globalNavRef} = useContext(GlobalLayoutRefsContext);
  const searchButtonRef = useRef(null);
  const browseDrawerRef = useRef(null);
  const browseButtonRef = useRef(null);
  const yourContentDrawerRef = useRef(null);
  const yourContentButtonRef = useRef(null);
  const binderDrawerRef = useRef(null);
  const binderButtonRef = useRef(null);
  const {width: windowWidth} = useWindowSize();
  const isDesktopDisplay = windowWidth > sm;
  const visuallyHideLabel = windowWidth < xs;

  useLayoutEffect(() => {
    if (windowWidth <= sm) {
      if (openIndex !== INACTIVE_INDEX_VALUE) {
        disableBodyScroll(ref.current);
      } else {
        enableBodyScroll(ref.current);
      }
    }
  }, [windowWidth, openIndex]);

  // Assign an index to each button in the global-navigation menu.
  const buttonIndices = {
    searchButtonIndex: 0,
    browseButtonIndex: 1,
    yourContentButtonIndex: 2,
    assignmentsButtonIndex: 3,
    binderButtonIndex: 4,
  };

  const focusRef = useFocusOut(() => {
    setOpenIndex(INACTIVE_INDEX_VALUE);
  }, openIndex >= 0);

  const handleOnEscape = (ref, buttonRef, setOpenIndex) => {
    // The search logic is distinct: if search is activated (the search form
    // is on display) hitting 'Escape' will return focus to the global-nav's
    // 'Search' button. The search content-drawer is anchored to the search
    // form (not the Search button...like the other content-drawers are) so
    // this distinction is necessary.
    if (openIndex === buttonIndices.searchButtonIndex) {
      buttonRef.current.focus();
    } else if (
      ref &&
      ref.current &&
      ref.current.contains(document.activeElement)
    ) {
      buttonRef.current.focus();
    }
    setOpenIndex();
  };

  const handleOnClickOff = (event, buttonRef) => {
    // Clicking into the search form's input-field  or submit button should not
    // trigger a click-off event.
    const searchIsActive = openIndex === buttonIndices.searchButtonIndex;
    const isSearchFormInputField = event.target.id === GLOBAL_NAV_SEARCH_FORM_ID;
    const searchFormSubmitButton = document.querySelector('.globalNavSearchSubmitButton');
    const isSearchFormSubmitButton = event.target === searchFormSubmitButton;
    const notAClickOffEvent = (
      searchIsActive && (isSearchFormInputField || isSearchFormSubmitButton)
    );

    if (!notAClickOffEvent && !buttonRef.current.contains(event.target)) {
      setOpenIndex(INACTIVE_INDEX_VALUE);
      buttonRef.current.focus();
    }
  };

  const handleOnCancel = (buttonRef) => {
    setOpenIndex(INACTIVE_INDEX_VALUE);
    buttonRef.current.focus();
  };

  const handleClick = (index) => {
    if (openIndex !== index) {
      setOpenIndex(index);
    } else {
      setOpenIndex(INACTIVE_INDEX_VALUE);
    }
  };

  const allCollections = useSelector((state) => getCollections(state));
  const {
    isAuthorizedDistrictLeader,
    isBinderApprovedPrincipal,
    isFreeUser,
    isParent,
    isStudent,
    isTeacher,
    isUserStateValidated,
    hasActiveProductLicense
  } = userProperties;

  const buttonProps = {
    __classNameFor: {root: css(buttonStyles)},
    flavor: Button.flavor.incognito,
  };

  history.listen(() => {
    setOpenIndex(INACTIVE_INDEX_VALUE);
  });

  const searchIsActive = openIndex === buttonIndices.searchButtonIndex;

  const inactiveLineItemStyle = (
    (
      searchIsActive &&
      isDesktopDisplay
    ) ?
      inactiveButtonStyle :
      null
  );

  const searchButton = (
    <li
      className={css(liStyle, inactiveLineItemStyle)}
      data-qa-selector="search_button_masthead"
    >
      <ButtonWithTrackEvent
        objectType="Content"
        actionPrefix={searchIsActive ? 'Cancel' : 'Initiate'}
        actionName="Search"
        legacyEventName="GlobalNav menu item-Toggle search"
        streamProperties={{description: `${searchIsActive ? 'Close' : 'Open'} Search input navigation dropdown menu.`}}
        ariaProps={{
          'aria-expanded': searchIsActive,
          'aria-haspopup': true,
        }}
        onClick={() => {
          const searchPageInputField = document.getElementById(AGATHA_PAGE_SEARCH_FORM_ID);

          if (
            window.location.pathname === searchRoot &&
            searchPageInputField
          ) {
            searchPageInputField.focus();
          } else {
            window.location.href = searchRoot;
          }
        }}
        ref={searchButtonRef}
        {...buttonProps}
      >
        <GlobalNavMenuItem
          displayChevron={false}
          iconElement={
            <SearchIcon
              aria-hidden="true"
              className={css(iconStyle(openIndex === buttonIndices.searchButtonIndex))}
              itemLabel="Search"
            />
          }
          isActive={openIndex === buttonIndices.searchButtonIndex}
          itemLabel="Search"
        />
      </ButtonWithTrackEvent>
      <GlobalNavSearch
        show={(openIndex === buttonIndices.searchButtonIndex && !isDesktopDisplay)}
        history={props.history}
        userProperties={userProperties}
        user={user}
        handleOnCancel={handleOnCancel}
        handleOnClickOff={handleOnClickOff}
        handleOnEscape={handleOnEscape}
        isDesktopDisplay={isDesktopDisplay}
        ref={{
          searchButtonRef: searchButtonRef,
          browseButtonRef: browseButtonRef,
        }}
        setOpenIndex={setOpenIndex}
      />
    </li>
  );

  const isContentVisible = (isSectionActive) =>
    Boolean(isSectionActive && globalNavRef.current);

  // Other roles won't return binder menu options
  const shouldHaveBinderOptions = (
    isStudent ||
    isTeacher ||
    (!isFreeUser && isAuthorizedDistrictLeader && hasActiveProductLicense) ||
    (!isFreeUser && isBinderApprovedPrincipal && isUserStateValidated && hasActiveProductLicense)
  );

  const browseIsActive = openIndex === buttonIndices.browseButtonIndex;

  const browseButton = (
    <li
      className={css(liStyle, inactiveLineItemStyle)}
      data-qa-selector="browse_button_masthead"
      ref={browseDrawerRef}
    >
      <ButtonWithPopOutWithTrackEvent
        id="globalNavBrowse"
        objectType="Content"
        actionPrefix={browseIsActive ? 'Cancel' : 'Initiate'}
        actionName="Navigate"
        legacyEventName="GlobalNav menu item-Toggle browse"
        streamProperties={{
          description: `${
            browseIsActive ? 'Close' : 'Open'
          } Browse dropdown navigation dropdown menu.`,
        }}
        ariaProps={{
          'aria-expanded': browseIsActive,
          'aria-haspopup': true,
        }}
        onClickButton={() => {
          handleClick(buttonIndices.browseButtonIndex);
        }}
        onClickOff={(e) => handleOnClickOff(e, browseButtonRef)}
        onEsc={() => {
          handleOnEscape(browseDrawerRef, browseButtonRef, () =>
            setOpenIndex(INACTIVE_INDEX_VALUE)
          );
        }}
        ref={browseButtonRef}
        isPopOutVisible={isContentVisible(browseIsActive)}
        __cssFor={buttonWithPopOutStyle}
        enableDrawerMode
        drawerButtonIcon={
          <GlobalNavMenuItem
            displayChevron={false}
            iconElement={
              <BrowseIcon
                aria-hidden="true"
                className={css(iconStyle(browseIsActive))}
                itemLabel="Browse"
              />
            }
          />
        }
        drawerButtonTitle={visuallyHideLabel ? '' : 'Browse'}
        buttonContents="Browse"
        popOutContents={
          <BrowseDrawer collections={allCollections} history={history} />
        }
      />
    </li>
  );
  const yourContentIsActive =
    openIndex === buttonIndices.yourContentButtonIndex;
  const yourContentButton = (
    <li
      className={css(liStyle, inactiveLineItemStyle)}
      data-qa-selector="your_content_button_masthead"
      ref={yourContentDrawerRef}
    >
      <ButtonWithPopOutWithTrackEvent
        id="globalNavYourContent"
        onClickButton={() => {
          handleClick(buttonIndices.yourContentButtonIndex);
        }}
        objectType="Content"
        actionPrefix={yourContentIsActive ? 'Cancel' : 'Initiate'}
        actionName="Navigate"
        legacyEventName="GlobalNav menu item-Toggle your content"
        streamProperties={{
          description: `${
            yourContentIsActive ? 'Close' : 'Open'
          } Your Content navigation dropdown menu.`,
        }}
        onClickOff={(e) => handleOnClickOff(e, yourContentButtonRef)}
        onEsc={() => {
          handleOnEscape(yourContentDrawerRef, yourContentButtonRef, () =>
            setOpenIndex(INACTIVE_INDEX_VALUE)
          );
        }}
        ref={yourContentButtonRef}
        isPopOutVisible={isContentVisible(yourContentIsActive)}
        enableDrawerMode
        drawerButtonIcon={
          <GlobalNavMenuItem
            displayChevron={false}
            iconElement={
              <YourContentIcon
                aria-hidden="true"
                className={css(iconStyle(yourContentIsActive))}
                itemLabel="Your Content"
              />
            }
          />
        }
        drawerButtonTitle={visuallyHideLabel ? '' : 'Your Content'}
        buttonContents="Your Content"
        __cssFor={buttonWithPopOutStyle}
        popOutContents={
          <YourContentDrawer
            history={history}
            userProperties={userProperties}
          />
        }
      />
    </li>
  );

  const shouldShowAssignmentsButton = isTeacher || isStudent || isParent;
  const {isAuthenticated} = userProperties;

  const assignmentsButton = shouldShowAssignmentsButton ? (
    <li className={css(liStyle, inactiveLineItemStyle)}>
      <AnchorWithTrackEvent
        eventProperties={{
          description: 'global nav assignments link',
        }}
        objectType="Assignment"
        actionName="Navigate"
        legacyEventName="GlobalNav menu item-Toggle assignments"
        streamProperties={{
          description: 'Global navigation Assignments link.',
        }}
        className={css(topLevelLinkStyle)}
        href={isStudent ? '/assignments' : '/binder/assignments'}
      >
        <GlobalNavMenuItem
          iconElement={
            <AssignmentsIcon
              aria-hidden="true"
              className={css(assignmentIconStyle)}
            />
          }
          itemLabel="Assignments"
          displayChevron={false}
        />
      </AnchorWithTrackEvent>
    </li>
  ) : null;

  const binderIsActive = openIndex === buttonIndices.binderButtonIndex;
  const binderButton = (
    <li className={css(liStyle, inactiveLineItemStyle)} ref={binderDrawerRef}>
      {shouldHaveBinderOptions ? (
        <ButtonWithPopOutWithTrackEvent
          id="globalNavBinder"
          onClickButton={() => {
            handleClick(buttonIndices.binderButtonIndex);
          }}
          actionPrefix={binderIsActive ? 'Cancel' : 'Initiate'}
          actionName="Navigate"
          legacyEventName="GlobalNav menu item-Toggle binder"
          streamProperties={{
            description: `${binderIsActive ? 'Close' : 'Open'
              } Binder navigation dropdown menu.`,
          }}
          onClickOff={(e) => handleOnClickOff(e, binderButtonRef)}
          onEsc={() => {
            handleOnEscape(binderDrawerRef, binderButtonRef, () =>
              setOpenIndex(INACTIVE_INDEX_VALUE)
            );
          }}
          ref={binderButtonRef}
          isPopOutVisible={isContentVisible(binderIsActive)}
          __cssFor={buttonWithPopOutStyle}
          enableDrawerMode
          drawerButtonIcon={
            <GlobalNavMenuItem
              displayChevron={false}
              iconElement={
                <BinderIcon
                  aria-hidden="true"
                  className={css(iconStyle(binderIsActive))}
                  itemLabel="Binder"
                />
              }
            />
          }
          drawerButtonTitle={visuallyHideLabel ? '' : 'Binder'}
          buttonContents="Binder"
          popOutContents={
            <BinderDrawer history={history} userProperties={userProperties} />
          }
        />
      ) : (
        <AnchorWithTrackEvent
          id="globalNavBinder"
          eventProperties={{
            description: 'global nav assignments link',
          }}
          objectType="Assignment"
          actionName="Navigate"
          legacyEventName="GlobalNav menu item-Toggle binder"
          streamProperties={{
            description: `${binderIsActive ? 'Close' : 'Open'
              } Binder navigation dropdown menu.`,
          }}
          className={css(topLevelLinkStyle)}
          href={isStudent ? '/assignments' : '/binder/'}
        >
          <GlobalNavMenuItem
            displayChevron={false}
            iconElement={
              <BinderIcon
                aria-hidden="true"
                className={css(iconStyle(binderIsActive))}
              />
            }
            itemLabel="Binder"
          />
        </AnchorWithTrackEvent>
      )}
    </li>
  );

  const globalNavButtons = (
    <>
      {searchButton}
      {browseButton}
      {isAuthenticated ? yourContentButton : null}
      {isAuthenticated ? assignmentsButton : null}
      {isAuthenticated ? binderButton : null}
    </>
  );

  const searchFeatureAndGlobalNavButtons = (
    <>
      <li className={css(liStyle, searchFormStyle)}>
        <GlobalNavSearch
          show={openIndex === buttonIndices.searchButtonIndex}
          history={props.history}
          userProperties={userProperties}
          user={user}
          handleOnClickOff={(event) => handleOnClickOff(event, searchButtonRef)}
          handleOnEscape={handleOnEscape}
          handle
          isDesktopDisplay={isDesktopDisplay}
          ref={{
            searchButtonRef: searchButtonRef,
            browseButtonRef: browseButtonRef,
          }}
          setOpenIndex={setOpenIndex}
          handleOnCancel={handleOnCancel}
        />
      </li>
      {globalNavButtons}
    </>
  );
  const globalNavDesktopDisplay =
    openIndex === buttonIndices.searchButtonIndex
      ? searchFeatureAndGlobalNavButtons
      : globalNavButtons;

  return (
    <nav ref={ref} className={css(globalNavStyle(hideGlobalNav))}>
      <ul ref={focusRef} className={css(ulStyle)}>
        {isDesktopDisplay ? globalNavDesktopDisplay : globalNavButtons}
      </ul>
    </nav>
  );
});

GlobalNav.propTypes = propTypes;
export default GlobalNav;
